import { SimulationConstants, SteamAchievements } from "../../shared/constants";
import { Debug } from "../../shared/debug";
import { getEntityById } from "../../shared/entityFactory";
import { GameMode } from "../../shared/game";
import { Player } from "../../shared/player";
import { TrainingLevel } from "../../shared/playerAccount";
import { getConnectedCells, getLeagueTier } from "../../shared/utils";
import { GameClient } from "./gameClient";
import { getLadderRank, setUserAchievement } from "./userSettings";

let maxCapacityTracker: number = 0;

export function onStartGame(game: GameClient, mode: GameMode) {
    //Check all end game achievements
    if (mode !== GameMode.Solo) {
        triggerAchievement(SteamAchievements.TriedMultiplayer);
    }

    if (game.trainingLevel === TrainingLevel.Trained) {
        triggerAchievement(SteamAchievements.PlayFirstGame);
    }

    maxCapacityTracker = 0;
}

export function onEndGame(game: GameClient, hasWon: boolean) {
    const isSolo = game.players.length === 1;
    const isRanked = game.players.length === 2 && game.players.some(player => player.isBot); //against a replay

    if (hasWon) {
        if (game.trainingLevel === TrainingLevel.Trained && isSolo) {
            triggerAchievement(SteamAchievements.WinFirstGame);
        }

        if (game.trainingLevel === TrainingLevel.MultiplayerIntro) {
            triggerAchievement(SteamAchievements.FinishTraining);
        }

        if (isSolo && game.endlessMode && game.getCurrentRound() >= SimulationConstants.MaxRoundsByTrainingLevel[TrainingLevel.Trained] + 4) {
            triggerAchievement(SteamAchievements.Round4Endless);
        }

        if (isSolo && maxCapacityTracker <= 10) {
            triggerAchievement(SteamAchievements.StayUnder10Cells);
        }

        const rank = isRanked && game.trainingLevel === TrainingLevel.Trained ? getLeagueTier(getLadderRank()) : 0;
        switch (rank) {
            case 1:
                triggerAchievement(SteamAchievements.ReachedRank1);
                break;
            case 2:
                triggerAchievement(SteamAchievements.ReachedRank2);
                break;
            case 3:
                triggerAchievement(SteamAchievements.ReachedRank3);
                break;
            case 4:
                triggerAchievement(SteamAchievements.ReachedRank4);
                break;
            case 5:
                triggerAchievement(SteamAchievements.ReachedRank5);
                break;
        }
    }
}

export function onStartShop(game: GameClient) {
    const player = game.getPlayer();
    if (!player) return;

    if (player.currentFunds >= 20) {
        triggerAchievement(SteamAchievements.LargeMoney);
    }

    for (const immuneCell of player.immuneCells) {
        const entity = getEntityById(immuneCell.id);
        if (entity) {
            const connectedCells = getConnectedCells(entity);
            if (connectedCells.length >= 8) {
                triggerAchievement(SteamAchievements.Connector);
            }
            break;
        }
    }
}

export function onSellImmuneCell(game: GameClient) {
    const player = game.getPlayer();
    if (!player) return;

    if (player.currentFunds >= 20) {
        triggerAchievement(SteamAchievements.LargeMoney);
    }
}

export function onEndCombat(game: GameClient) {
    const player = game.getPlayer();
    if (!player) return;

    const hp = game.getRoundStat(player.id, 'hpGained');
    if (hp >= 100) {
        triggerAchievement(SteamAchievements.LargeHealth);
    }

    const maxSingleExplosionKills = game.getRoundStat(player.id, 'maxSingleExplosionKills');
    if (maxSingleExplosionKills >= 20) {
        triggerAchievement(SteamAchievements.BigExplosion);
    }

    const maxElectricityVirusKills = game.getRoundStat(player.id, 'electrictyVirusKills');
    if (maxElectricityVirusKills >= 100) {
        triggerAchievement(SteamAchievements.BigShock);
    }

    const summonedCells = game.getRoundStat(player.id, 'immuneCellsSpawnedInCombat');
    if (summonedCells >= 10) {
        triggerAchievement(SteamAchievements.Summoner);
    }

    //End of shop would make more sense but can skip adding an extra event hook and do it here, should be fine
    if (player.immuneCells.length > maxCapacityTracker) {
        maxCapacityTracker = player.immuneCells.length;
    }
}

function getElectronApi(): any {
    if (!window) {
        return undefined;
    }
    return (window as any).electronApi;
}

function triggerAchievement(achievement: SteamAchievements) {
    if (Debug.NoAchievements) return;
    setUserAchievement(achievement); // does nothing if already have it
    const electronApi = getElectronApi();
    if (electronApi) {
        electronApi.triggerAchievement(achievement);
    }
}
